import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { HeritageSeal, Kicker } from "@/components/primitives";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth, formatApiError } from "@/lib/auth";
import { ArrowLeft, LogIn, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { user, signIn, signUp } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    garden_name: "",
    location: "",
  });

  if (user && user !== null && user !== false) {
    const redirect = loc.state?.from && loc.state.from !== "/auth" ? loc.state.from : "/home";
    return <Navigate to={redirect} replace />;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.email || !form.password) { setErr("Email and password are required"); return; }
    if (mode === "signup" && !form.name.trim()) { setErr("A name to greet you by"); return; }
    if (form.password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      if (mode === "signin") {
        await signIn(form.email.trim(), form.password);
        toast.success("Welcome back");
      } else {
        await signUp({
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim(),
          garden_name: form.garden_name.trim() || null,
          location: form.location.trim() || null,
        });
        toast.success(`Welcome, ${form.name.split(" ")[0] || "gardener"}`);
      }
      nav("/home", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail, e2.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-8" data-testid="auth-page">
      <Link to="/" className="self-start chip mb-6 max-w-3xl w-full sm:w-auto" data-testid="auth-back">
        <ArrowLeft size={14} /> Back
      </Link>

      <div
        className="h-card w-full max-w-md px-6 sm:px-10 py-10 shadow-seal animate-bloom-in"
        data-testid="auth-card"
      >
        <div className="flex justify-center mb-5">
          <BrandLogo size={92} />
        </div>
        <Kicker>{mode === "signin" ? "Welcome Back" : "A New Gardener"}</Kicker>
        <h1 className="font-display text-4xl sm:text-5xl text-sage-dark text-center mt-3 leading-tight">
          {mode === "signin" ? "Sign in" : "Create your patch"}
        </h1>
        <p className="text-center font-display italic text-ink-soft mt-2">
          {mode === "signin"
            ? "Step back into your garden."
            : "A few quick details, then we'll get to planting."}
        </p>

        {/* Mode tabs */}
        <div className="mt-6 flex gap-2 justify-center">
          <button
            type="button"
            className="chip"
            data-active={mode === "signin"}
            onClick={() => { setMode("signin"); setErr(""); }}
            data-testid="auth-tab-signin"
          >
            <LogIn size={13} /> Sign in
          </button>
          <button
            type="button"
            className="chip"
            data-active={mode === "signup"}
            onClick={() => { setMode("signup"); setErr(""); }}
            data-testid="auth-tab-signup"
          >
            <UserPlus size={13} /> Create account
          </button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="auth-form">
          {mode === "signup" && (
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Your name *</span>
              <input
                className="field"
                placeholder="e.g., Margaret"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="auth-name"
                required
                autoComplete="name"
              />
            </label>
          )}
          <label className="block">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Email *</span>
            <input
              type="email"
              className="field"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              data-testid="auth-email"
              required
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Password *</span>
            <input
              type="password"
              className="field"
              placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              data-testid="auth-password"
              required
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
            />
          </label>

          {mode === "signin" && (
            <div className="text-right -mt-1">
              <Link to="/forgot-password" className="text-sm underline text-sage-dark hover:text-ink" data-testid="auth-forgot-link">
                Forgot password?
              </Link>
            </div>
          )}

          {mode === "signup" && (
            <>
              <label className="block">
                <span className="text-sm text-ink-soft mb-1.5 inline-block">Garden name (optional)</span>
                <input
                  className="field"
                  placeholder="e.g., The Mangaroa Patch"
                  value={form.garden_name}
                  onChange={(e) => set("garden_name", e.target.value)}
                  data-testid="auth-garden-name"
                />
              </label>
              <label className="block">
                <span className="text-sm text-ink-soft mb-1.5 inline-block">Where you garden (optional)</span>
                <input
                  className="field"
                  placeholder="e.g., Wellington, NZ"
                  value={form.location}
                  onChange={(e) => set("location", e.target.value)}
                  data-testid="auth-location"
                />
              </label>
            </>
          )}

          {err && (
            <div className="bg-cream-deep border border-heritage-border rounded-xl px-4 py-3 text-sm text-terracotta-dark" data-testid="auth-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            className="btn-heritage w-full mt-2"
            disabled={busy}
            data-testid="auth-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : mode === "signin" ? <LogIn size={16} /> : <UserPlus size={16} />}
            {busy ? "Just a moment..." : mode === "signin" ? "Sign in" : "Enter the patch"}
          </button>
        </form>

        <p className="text-center text-sm text-ink-soft mt-6">
          {mode === "signin" ? (
            <>
              New to the patch?{" "}
              <button type="button" onClick={() => { setMode("signup"); setErr(""); }} className="underline text-sage-dark hover:text-ink font-semibold" data-testid="auth-switch-signup">
                Create an account
              </button>
            </>
          ) : (
            <>
              Already a keeper?{" "}
              <button type="button" onClick={() => { setMode("signin"); setErr(""); }} className="underline text-sage-dark hover:text-ink font-semibold" data-testid="auth-switch-signin">
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
